import { useCallback, useEffect, useRef, useState } from 'react';

import { useStores } from '~store';
import {
  addConversationTab,
  addDraftTab,
  readStoredTabs,
  reconcileTabs,
  TreadAgentTab,
  writeStoredTabs,
} from '~tread-agent/treadAgentTabState';

interface UseTreadAgentTabsParams {
  companyId: string;
  isPanelMinimized: boolean;
  isPanelOpen: boolean;
  onOpenPanel: () => void;
  onLastTabClosed: () => void;
}

export default function useTreadAgentTabs(params: UseTreadAgentTabsParams) {
  const { companyId, isPanelMinimized, isPanelOpen, onLastTabClosed, onOpenPanel } =
    params;
  const { treadAgentStore } = useStores();
  const activeConversationId = treadAgentStore.activeConversationId;

  const [tabState, setTabState] = useState(() => ({
    companyId,
    tabs: readStoredTabs(companyId),
  }));

  if (tabState.companyId !== companyId) {
    setTabState({ companyId, tabs: readStoredTabs(companyId) });
  }

  const tabs = tabState.companyId === companyId ? tabState.tabs : [];

  const setTabs = useCallback(
    (update: (previousTabs: TreadAgentTab[]) => TreadAgentTab[]) => {
      setTabState((previous) => ({ ...previous, tabs: update(previous.tabs) }));
    },
    [],
  );

  const previousCompanyIdRef = useRef(companyId);
  useEffect(() => {
    const companyChanged = previousCompanyIdRef.current !== companyId;
    previousCompanyIdRef.current = companyId;

    if (companyChanged) {
      treadAgentStore.closeConversation();
    }

    setTabs((previousTabs) =>
      reconcileTabs(
        previousTabs,
        companyChanged ? null : activeConversationId,
        isPanelOpen,
      ),
    );
  }, [activeConversationId, companyId, isPanelOpen, setTabs]);

  useEffect(() => {
    writeStoredTabs(tabState.companyId, tabState.tabs);
  }, [tabState]);

  const isTabCurrent = useCallback(
    (tab: TreadAgentTab) => isPanelOpen && tab.conversationId === activeConversationId,
    [activeConversationId, isPanelOpen],
  );

  const isTabActive = useCallback(
    (tab: TreadAgentTab) => !isPanelMinimized && isTabCurrent(tab),
    [isPanelMinimized, isTabCurrent],
  );

  const selectTab = useCallback(
    (tab: TreadAgentTab) => {
      if (tab.conversationId) {
        treadAgentStore.openConversation(companyId, tab.conversationId);
      } else {
        treadAgentStore.closeConversation();
      }

      onOpenPanel();
    },
    [companyId, onOpenPanel, treadAgentStore],
  );

  const openConversationTab = useCallback(
    (conversationId: string) => {
      setTabs((previousTabs) => addConversationTab(previousTabs, conversationId));
      selectTab({ conversationId });
    },
    [selectTab, setTabs],
  );

  const openDraftTab = useCallback(() => {
    setTabs(addDraftTab);
    selectTab({ conversationId: null });
  }, [selectTab, setTabs]);

  const closeTab = useCallback(
    (tab: TreadAgentTab) => {
      const closedIndex = tabs.indexOf(tab);
      const remainingTabs = tabs.filter((openTab) => openTab !== tab);
      setTabs(() => remainingTabs);

      if (!isTabCurrent(tab)) {
        return;
      }

      if (isPanelMinimized) {
        onLastTabClosed();

        return;
      }

      const neighborTab = remainingTabs[closedIndex] ?? remainingTabs[closedIndex - 1];

      if (neighborTab) {
        selectTab(neighborTab);
      } else {
        onLastTabClosed();
      }
    },
    [isPanelMinimized, isTabCurrent, onLastTabClosed, selectTab, setTabs, tabs],
  );

  return { closeTab, isTabActive, openConversationTab, openDraftTab, selectTab, tabs };
}
